import React, { useEffect, useState } from 'react';
import { Button, MenuItem, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { StaffDailyHours, formatDailyHours, parseDailyHours } from '../utils/staffDailyHours';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(0.5, 0),
    color: theme.palette.common.white,
  },
  controls: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 40,
  },
  button: { minHeight: 40 },
  input: {
    width: 128,
    '& .MuiInputBase-root': { height: 40 },
    '& .MuiInputBase-root, & .MuiInputLabel-root, & .MuiSelect-icon': {
      color: theme.palette.common.white,
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.grey[500] },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.common.white },
    '& .Mui-error': { color: theme.palette.error.main },
  },
  menu: {
    maxHeight: 288,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
  },
  option: { minHeight: 36, fontSize: 14 },
  error: { width: 0, minWidth: '100%', marginTop: theme.spacing(0.5), fontSize: 12, textAlign: 'right' },
  screenReaderOnly: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
}));

const QUARTER_HOURS = Array.from({ length: 95 }, (_, index) => {
  const minutes = (index + 1) * 15;
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
});

interface Props {
  value: StaffDailyHours | null;
  onApply: (value: StaffDailyHours | null) => void;
}

const StaffDailyHoursFilter: React.FC<Props> = ({ value, onApply }) => {
  const classes = useStyles();
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  useEffect(() => {
    const [from, to] = value ? formatDailyHours(value).split('–') : ['', ''];
    setStart(from === '00:00' ? '' : from);
    setEnd(to === '24:00' ? '' : to);
  }, [value]);
  let draft: StaffDailyHours | null = null;
  let error = '';
  try {
    draft = parseDailyHours(start, end);
  } catch (e) {
    error = (e as Error).message;
  }
  const dirty = draft?.dailyStartMinute !== value?.dailyStartMinute || draft?.dailyEndMinute !== value?.dailyEndMinute;

  return (
    <form
      className={classes.root}
      aria-label="Daily hours filter"
      onSubmit={event => {
        event.preventDefault();
        if (!error) onApply(draft);
      }}
    >
      <div className={classes.controls}>
        <Typography variant="body2" className={classes.label}>
          Daily hours
        </Typography>
        {(['start', 'end'] as const).map(boundary => {
          const isStart = boundary === 'start';
          const dayBoundary = isStart ? '00:00' : '24:00';
          return (
            <TextField
              key={boundary}
              id={`daily-hours-${boundary}`}
              className={classes.input}
              select
              label={isStart ? 'From' : 'To'}
              variant="outlined"
              size="small"
              value={isStart ? start : end}
              onChange={event => (isStart ? setStart : setEnd)(event.target.value)}
              InputLabelProps={{ shrink: true }}
              SelectProps={{
                displayEmpty: true,
                renderValue: selected => (selected as string) || dayBoundary,
                SelectDisplayProps: {
                  id: `daily-hours-${boundary}-button`,
                  'aria-describedby': error ? 'daily-hours-error' : undefined,
                  'aria-invalid': !!error,
                },
                MenuProps: {
                  classes: { paper: classes.menu },
                  getContentAnchorEl: null,
                  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                },
              }}
              error={!!error}
            >
              {isStart && (
                <MenuItem className={classes.option} value="">
                  00:00
                </MenuItem>
              )}
              {QUARTER_HOURS.map(time => (
                <MenuItem className={classes.option} key={time} value={time}>
                  {time}
                </MenuItem>
              ))}
              {!isStart && (
                <MenuItem className={classes.option} value="">
                  24:00
                </MenuItem>
              )}
            </TextField>
          );
        })}
        <Button
          className={classes.button}
          type="submit"
          variant="contained"
          color="primary"
          disabled={!!error || !dirty}
        >
          Apply
        </Button>
        <Button
          className={classes.button}
          type="button"
          color="inherit"
          disabled={!start && !end}
          onClick={() => {
            setStart('');
            setEnd('');
          }}
        >
          All day
        </Button>
      </div>
      {error && (
        <Typography id="daily-hours-error" variant="body2" className={classes.error} color="error">
          {error}
        </Typography>
      )}
      <span className={classes.screenReaderOnly} role="status" aria-live="polite" aria-atomic="true">
        Applied daily hours: {value ? formatDailyHours(value) : 'All day'}
      </span>
    </form>
  );
};

export default StaffDailyHoursFilter;
